import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { Quote } from '../../../../../../../Typescript/backend/classes';
import { BackendCompletedForm } from '../../../../../../../Typescript/backend/classes';
import { QuestionAnswerDisplay } from '../../../../../../common/QuestionAnswerDisplay';
import {
  StyledAccordion,
  StyledFormComparisonBox,
  StyledFormBox,
  StyledFormTitle,
  StyledNoFormBox,
  DLStyle,
  TwoColumnContainer,
  ColumnContainer,
  StyledColumnTitle,
} from './sharedComponents';

interface AnswersAccordionProps {
  quote?: Quote;
  comparisonQuote: Quote | null;
  loading?: boolean;
}

/**
 * Sort form comparison objects by policy
 * Order: General Information, General Liability, Property, then others alphabetically
 */
const sortFormComparisons = (
  formComparisons: Array<{
    form?: BackendCompletedForm;
    comparisonForm?: BackendCompletedForm;
  }>,
) => {
  const generalInformation = formComparisons.filter(
    (fc) => getPolicy(fc) === null,
  );

  const generalLiability = formComparisons.filter(
    (fc) => getPolicy(fc)?.displayName === 'General Liability',
  );
  const property = formComparisons.filter(
    (fc) => getPolicy(fc)?.displayName === 'Property',
  );
  const formsFound = [...generalInformation, ...generalLiability, ...property];
  const otherForms = formComparisons
    .filter(
      (fc) =>
        formsFound.find(
          (innerFc) =>
            fc?.form && innerFc?.form && fc.form.matchByPolicy(innerFc.form),
        ) === undefined,
    )
    .sort((a, b) => {
      const policyA = getPolicy(a);
      const policyB = getPolicy(b);
      return policyA && policyB
        ? policyA.displayName.localeCompare(policyB.displayName)
        : 0;
    });

  return [...formsFound, ...otherForms];
};

const getPolicy = (fc: {
  form?: BackendCompletedForm;
  comparisonForm?: BackendCompletedForm;
}) => fc.form?.policy ?? fc.comparisonForm?.policy ?? null;

/**
 * Collect all unique form types from both quotes, matching by policy
 */
const getAllFormTypes = (
  quote?: Quote,
  comparisonQuote?: Quote | null,
): Array<{
  form?: BackendCompletedForm;
  comparisonForm?: BackendCompletedForm;
}> => {
  const quoteForms = quote?.completedForms || [];
  const comparisonForms = comparisonQuote?.completedForms || [];

  // Create a map to track forms by policy key
  // Key format: policy ID if exists, otherwise 'general' for null policy
  let formMap = quoteForms.reduce((acc, form) => {
    const key = form.policy?.id || 'general';
    let formComparison = acc.get(key);
    if (!formComparison) {
      formComparison = {};
      acc.set(key, formComparison);
    }
    formComparison.form = form;
    return acc;
  }, new Map<string, { form?: BackendCompletedForm; comparisonForm?: BackendCompletedForm }>());

  // Add forms from the comparison quote
  formMap = comparisonForms.reduce((acc, form) => {
    const key = form.policy?.id || 'general';
    let formComparison = acc.get(key);
    if (!formComparison) {
      formComparison = {};
      acc.set(key, formComparison);
    }
    formComparison.comparisonForm = form;
    return acc;
  }, formMap);

  // Get the result as an array
  const formComparisons = Array.from(formMap.values());

  // Sort the result by passing the policy itself to the sorting function
  return sortFormComparisons(formComparisons);
};

const FormSkeleton = () => {
  return (
    <StyledFormBox>
      <Skeleton variant='rectangular' width='60%' height={24} sx={{ mb: 2 }} />
      {[...Array(5)].map((_, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          <Skeleton
            variant='rectangular'
            width='80%'
            height={20}
            sx={{ mb: 1 }}
          />
          <Skeleton variant='rectangular' width='60%' height={20} />
        </Box>
      ))}
    </StyledFormBox>
  );
};

interface CompareFormsProps {
  form?: BackendCompletedForm;
  comparisonForm?: BackendCompletedForm;
  quote?: Quote;
  comparisonQuote?: Quote | null;
  loading?: boolean;
}

const CompareForms = ({
  form,
  comparisonForm,
  quote,
  comparisonQuote,
  loading,
}: CompareFormsProps) => {
  const comparisonQuoteLabel =
    comparisonQuote?.friendlyId ||
    comparisonQuote?.uniqueId ||
    'Comparison Quote';
  const quoteLabel = quote?.friendlyId || quote?.uniqueId || 'Quote';

  return (
    <TwoColumnContainer>
      <ColumnContainer>
        <StyledColumnTitle variant='subtitle1'>
          {comparisonQuoteLabel}
        </StyledColumnTitle>
        {loading ? (
          <FormSkeleton />
        ) : comparisonForm ? (
          <StyledFormBox>
            <StyledFormTitle variant='subtitle2'>
              {comparisonForm.label()}
            </StyledFormTitle>
            <DLStyle>
              {comparisonForm.answers.map((answer, _index, answers) => (
                <QuestionAnswerDisplay
                  key={answer.id}
                  answer={answer}
                  answers={answers}
                  indent={3}
                />
              ))}
            </DLStyle>
          </StyledFormBox>
        ) : (
          <NoForm />
        )}
      </ColumnContainer>
      <ColumnContainer>
        <StyledColumnTitle variant='subtitle1'>{quoteLabel}</StyledColumnTitle>
        {form ? (
          <StyledFormBox>
            <StyledFormTitle variant='subtitle2'>
              {form.label()}
            </StyledFormTitle>
            <DLStyle>
              {form.answers.map((answer, _index, answers) => (
                <QuestionAnswerDisplay
                  key={answer.id}
                  answer={answer}
                  answers={answers}
                  indent={3}
                />
              ))}
            </DLStyle>
          </StyledFormBox>
        ) : (
          <NoForm />
        )}
      </ColumnContainer>
    </TwoColumnContainer>
  );
};

const NoForm = () => {
  return (
    <StyledNoFormBox>
      <Typography variant='body2' color='text.secondary'>
        No matching answers found
      </Typography>
    </StyledNoFormBox>
  );
};

export const AnswersAccordion = ({
  quote,
  comparisonQuote,
  loading,
}: AnswersAccordionProps) => {
  const allFormTypes = getAllFormTypes(quote, comparisonQuote);

  return (
    <StyledAccordion elevation={0}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant='h6'>Answers</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {allFormTypes.map(({ form, comparisonForm }, index) => (
          <StyledFormComparisonBox key={index}>
            <CompareForms
              form={form}
              comparisonForm={comparisonForm}
              quote={quote}
              comparisonQuote={comparisonQuote}
              loading={loading}
            />
          </StyledFormComparisonBox>
        ))}
      </AccordionDetails>
    </StyledAccordion>
  );
};
